'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SidebarGroupId } from './navGroups';

const DRAG_THRESHOLD = 5;

export type DragRegion = SidebarGroupId | 'pins';
type DragState = { id: string; region: DragRegion; from: number; to: number; dy: number; span: number };
type DragPress = {
  id: string;
  region: DragRegion;
  from: number;
  startY: number;
  pointerId: number;
  element: HTMLElement;
  centers: number[];
  span: number;
};

/** Owns the sidebar's pointer gesture; the surface converts group slots to stored order indices. */
export function useNavDragReorder({
  drawer, regionEntries, onReorder,
}: {
  drawer: boolean;
  regionEntries: (region: DragRegion) => readonly { id?: string }[];
  onReorder: (id: string, region: DragRegion, slot: number) => void;
}) {
  // Desktop keeps the direct-manipulation contract the column has always had: hold the primary pointer
  // and move a row. Touch remains scrolling (and uses the context menu's keyboard-equivalent move
  // actions), while the sheet never rearranges underneath a finger trying to dismiss or navigate it.
  const entryRefs = useRef(new Map<string, HTMLElement>());
  const pressRef = useRef<DragPress | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const suppressClick = useRef(false);
  const [drag, setDrag] = useState<DragState | null>(null);

  const releaseDrag = useCallback(() => {
    const press = pressRef.current;
    const captured = !!dragRef.current;
    // Clear first: an explicit release emits `lostpointercapture`, whose handler must observe no live drag
    // rather than re-entering this cleanup against the same pointer.
    pressRef.current = null;
    dragRef.current = null;
    setDrag(null);
    if (press && captured) {
      try { press.element.releasePointerCapture(press.pointerId); } catch { /* capture already gone */ }
    }
  }, []);
  const onEntryPointerDown = (event: React.PointerEvent<HTMLElement>, entry: { id?: string }, region: DragRegion, slot: number) => {
    event.stopPropagation();
    if (drawer || !entry.id || event.pointerType === 'touch' || (event.pointerType === 'mouse' && event.button !== 0)) return;
    suppressClick.current = false;
    const entries = regionEntries(region);
    const centers = entries.map((candidate) => {
      const element = candidate.id ? entryRefs.current.get(candidate.id) : undefined;
      if (!element) return null;
      const rect = element.getBoundingClientRect();
      return rect.top + rect.height / 2;
    });
    if (centers.some((center) => center === null)) return;
    const ownRect = event.currentTarget.getBoundingClientRect();
    const numericCenters = centers as number[];
    const neighbour = numericCenters[slot + 1] ?? numericCenters[slot - 1];
    pressRef.current = {
      id: entry.id,
      region,
      from: slot,
      startY: event.clientY,
      pointerId: event.pointerId,
      element: event.currentTarget,
      centers: numericCenters,
      span: neighbour === undefined ? ownRect.height : Math.abs(neighbour - numericCenters[slot]),
    };
  };
  const onEntryPointerMove = (event: React.PointerEvent<HTMLElement>) => {
    const press = pressRef.current;
    if (!press) return;
    const dy = event.clientY - press.startY;
    if (!dragRef.current && Math.abs(dy) < DRAG_THRESHOLD) return;
    if (!dragRef.current) {
      try { press.element.setPointerCapture(press.pointerId); } catch { /* pointer already gone */ }
    }
    const carried = press.centers[press.from] + dy;
    let to = press.from;
    let nearest = Number.POSITIVE_INFINITY;
    press.centers.forEach((center, index) => {
      const distance = Math.abs(center - carried);
      if (distance < nearest) { nearest = distance; to = index; }
    });
    const next = { id: press.id, region: press.region, from: press.from, to, dy, span: press.span };
    dragRef.current = next;
    setDrag(next);
  };
  const onEntryPointerUp = () => {
    const finished = dragRef.current;
    if (!finished) { pressRef.current = null; return; }
    suppressClick.current = true;
    releaseDrag();
    if (finished.to !== finished.from) onReorder(finished.id, finished.region, finished.to);
  };
  useEffect(() => {
    const clearUncapturedPress = () => {
      if (!dragRef.current) pressRef.current = null;
    };
    window.addEventListener('pointerup', clearUncapturedPress);
    window.addEventListener('pointercancel', releaseDrag);
    return () => {
      window.removeEventListener('pointerup', clearUncapturedPress);
      window.removeEventListener('pointercancel', releaseDrag);
      releaseDrag();
    };
  }, [releaseDrag]);

  const entryProps = (entry: { id?: string }, region: DragRegion, slot: number) => {
    const id = entry.id;
    const moving = !!id && drag?.id === id;
    let shift = 0;
    if (drag?.region === region) {
      if (moving) shift = drag.dy;
      else if (drag.from < drag.to && slot > drag.from && slot <= drag.to) shift = -drag.span;
      else if (drag.from > drag.to && slot < drag.from && slot >= drag.to) shift = drag.span;
    }
    return {
      ref: (element: HTMLLIElement | null) => {
        if (id && element) entryRefs.current.set(id, element);
        else if (id) entryRefs.current.delete(id);
      },
      'data-dragging': moving || undefined,
      style: shift ? { transform: `translateY(${shift}px)` } : undefined,
      onPointerDown: (event: React.PointerEvent<HTMLElement>) => onEntryPointerDown(event, entry, region, slot),
      onLostPointerCapture: () => { if (dragRef.current) releaseDrag(); },
      onClickCapture: (event: React.MouseEvent<HTMLElement>) => {
        if (!suppressClick.current) return;
        suppressClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      },
    };
  };

  return {
    entryProps,
    surfaceProps: {
      'data-drag': drag ? true : undefined,
      onPointerMove: onEntryPointerMove,
      onPointerUp: onEntryPointerUp,
      onPointerCancel: releaseDrag,
    },
  };
}
